import type { ReactNode } from "react";

const LABEL = "text-[12.5px] font-[650] tracking-[0.08em] uppercase";

/**
 * A stat tile (section 11.0): an uppercase label on top, a big number at the
 * bottom with a short note beside it. `aside` sits opposite the label.
 *
 * The number is 52 px, smaller only when its tile is too narrow for it: four
 * tiles beside the hero, from 1100 px (v0.9.0's Open tickets tile), leave
 * each about 150 px there. It's sized by the tile's own width (a container
 * query) and its length, so a long day's "10h 45m" stays inside it, and a
 * short "3" keeps its size.
 */
export function StatTile({
  label,
  value,
  note,
  aside,
  children,
}: {
  label: string;
  value: string;
  note?: string | undefined;
  aside?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="flex min-h-[120px] min-w-0 flex-1 flex-col justify-between gap-4 rounded-panel border border-line bg-panel px-[22px] py-5 [container-type:inline-size]"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className={`${LABEL} text-muted`}>{label}</span>
        {aside}
      </div>
      <div className="flex flex-col gap-2.5">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span
            className="leading-none font-semibold tracking-[-0.05em] whitespace-nowrap"
            style={{ fontSize: `min(52px, ${String(Math.floor(180 / Math.max(3, value.length)))}cqi)` }}
          >
            {value}
          </span>
          {note ? <span className="text-[13px] text-muted">{note}</span> : null}
        </div>
        {children}
      </div>
    </div>
  );
}

/** The hero tile (section 11.0): solid Axon blue with a lighter glow in one corner. */
export function HeroTile({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div
      role="group"
      aria-label={label}
      className="flex min-h-[120px] shrink-0 flex-col justify-between gap-4 rounded-panel bg-brand bg-[radial-gradient(90%_130%_at_100%_0%,var(--hero-glow),transparent_60%)] px-6 py-5 text-white shadow-[0_12px_28px_var(--brand-shadow)] min-[1100px]:w-[420px]"
    >
      <span className={`${LABEL} text-white/88`}>{label}</span>
      {/* The number never wraps (#245): from 1100 px the tile is 420 px wide,
          and its row at least 158 px tall. On a day of 10h or more the note
          wraps instead, beside the number. */}
      <div className="flex items-end justify-between gap-3">
        <span className="text-[84px] leading-[0.9] font-semibold tracking-[-0.055em] whitespace-nowrap">{value}</span>
        {note ? <span className="pb-1.5 text-right text-[13px] text-white/88">{note}</span> : null}
      </div>
    </div>
  );
}

/** A thin rounded progress bar in the brand color. */
export function ProgressBar({ percent, label }: { percent: number; label: string }) {
  const clamped = Math.min(100, Math.max(0, Math.round(percent)));
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={clamped}
      className="h-1.5 overflow-hidden rounded-pill bg-track"
    >
      <div className="h-full rounded-pill bg-brand" style={{ width: `${String(clamped)}%` }} />
    </div>
  );
}
